import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';

/**
 * RowIconButton — the one icon-only action in a table or list row (Antonin, 14 Oct: "One table icon button").
 *
 * 32 px square, white fill, a grey-300 edge, grey hover (edge to grey-400, press grey-200), a 16 px Lucide icon
 * in secondary ink. Destructive is red; a status shortcut keeps its status colour. It carries an instant tooltip
 * (the same text as the aria-label) and never a native `title`.
 *
 * Same recipe as the practice app's `ROW_ICON_BUTTON`.
 */
export const ROW_ICON_BUTTON =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-grey-300 bg-surface-card transition-colors hover:border-grey-400 hover:bg-grey-100 active:bg-grey-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-50';

export type RowIconTone = 'default' | 'destructive' | 'success' | 'warning' | 'info';

const TONE_INK: Record<RowIconTone, string> = {
  default: 'text-text-secondary',
  destructive: 'text-status-error',
  success: 'text-status-success',
  warning: 'text-status-warning',
  info: 'text-status-info',
};

export interface RowIconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title' | 'aria-label'> {
  /** The action's name: the accessible name and the tooltip text. */
  label: string;
  /** Tooltip text when it should be shorter than the accessible name ("Edit macro" for "Edit macro Greeting"). */
  tooltip?: string;
  /** `destructive` is red; `success` / `warning` / `info` are status shortcuts that keep their status colour. */
  tone?: RowIconTone;
  /** A 16 px Lucide icon (`className="size-4"`, `aria-hidden`). */
  children?: ReactNode;
}

/** Forwards `ref` and every prop, so a popover / menu trigger can take it as its `render` element. */
export const RowIconButton = forwardRef<HTMLButtonElement, RowIconButtonProps>(function RowIconButton(
  { label, tooltip, tone = 'default', className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <Tooltip>
      <TooltipTrigger
        ref={ref}
        delay={0}
        closeDelay={0}
        render={<button {...rest} type={type} aria-label={label} className={cn(ROW_ICON_BUTTON, TONE_INK[tone], className)} />}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>{tooltip ?? label}</TooltipContent>
    </Tooltip>
  );
});
